// Consolidated filter-option loading + cascading (block -> GP -> village).
// Replaces the copy-pasted fetch logic that lived in Dashboard / Analytics /
// Subhadra / Member / CascadingFilter.
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from '../config';

const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

// apiBase lets a caller point this at a different API than the Express
// backend — nothing does today (Dashboard/Analytics moved back onto the
// backend), but the hook stays parameterized; omitting it keeps the default.
export default function useFilterOptions(apiBase = config.apiUrl) {
  const API_URL = apiBase;
  const [options, setOptions] = useState({
    blocks: [],
    gramPanchayats: [],
    villages: [],
    enumerators: [],
  });
  const [cascadingGPs, setCascadingGPs] = useState([]);
  const [cascadingVillages, setCascadingVillages] = useState([]);
  const [loadingCascading, setLoadingCascading] = useState(false);
  const baseRef = useRef({ gramPanchayats: [], villages: [] });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [loc, enums] = await Promise.all([
          axios.get(`${API_URL}/filters`),
          axios.get(`${API_URL}/enumerators`),
        ]);
        if (!active) return;
        const blocks = toOptions(loc.data?.blocks);
        const gramPanchayats = toOptions(loc.data?.gramPanchayats);
        const villages = toOptions(loc.data?.villages);
        const enumerators = toOptions(enums.data?.enumerators);
        setOptions({ blocks, gramPanchayats, villages, enumerators });
        setCascadingGPs(gramPanchayats);
        setCascadingVillages(villages);
        baseRef.current = { gramPanchayats, villages };
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    })();
    return () => { active = false; };
  }, [API_URL]);

  // selectedBlocks: array of option objects
  const fetchGPsByBlocks = useCallback(async (selectedBlocks) => {
    if (!selectedBlocks || selectedBlocks.length === 0) {
      setCascadingGPs(baseRef.current.gramPanchayats);
      return;
    }
    setLoadingCascading(true);
    try {
      const blocksParam = selectedBlocks.map((b) => b.value).join(',');
      const res = await axios.get(`${API_URL}/gram-panchayats-by-block`, { params: { blocks: blocksParam } });
      if (res.data?.success) setCascadingGPs(toOptions(res.data.gramPanchayats));
    } catch (err) {
      console.error('Error fetching gram panchayats:', err);
      setCascadingGPs(baseRef.current.gramPanchayats);
    } finally {
      setLoadingCascading(false);
    }
  }, [API_URL]);

  const fetchVillagesByFilters = useCallback(async (selectedBlocks, selectedGPs) => {
    const noBlocks = !selectedBlocks || selectedBlocks.length === 0;
    const noGPs = !selectedGPs || selectedGPs.length === 0;
    if (noBlocks && noGPs) {
      setCascadingVillages(baseRef.current.villages);
      return;
    }
    setLoadingCascading(true);
    try {
      const params = {};
      if (!noBlocks) params.blocks = selectedBlocks.map((b) => b.value).join(',');
      if (!noGPs) params.gramPanchayats = selectedGPs.map((g) => g.value).join(',');
      const res = await axios.get(`${API_URL}/villages-by-filters`, { params });
      if (res.data?.success) setCascadingVillages(toOptions(res.data.villages));
    } catch (err) {
      console.error('Error fetching villages:', err);
      setCascadingVillages(baseRef.current.villages);
    } finally {
      setLoadingCascading(false);
    }
  }, [API_URL]);

  const resetCascading = useCallback(() => {
    setCascadingGPs(baseRef.current.gramPanchayats);
    setCascadingVillages(baseRef.current.villages);
  }, []);

  return {
    options,
    cascadingGPs,
    cascadingVillages,
    loadingCascading,
    fetchGPsByBlocks,
    fetchVillagesByFilters,
    resetCascading,
    setCascadingVillages,
  };
}
